import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import moment from "moment";
import { useTheme } from "../../../contexts/Theme/ThemeContext";
import { AssinaturaApi } from "../../../hooks/assinaturaApi";
import { InfoAssinatura, Fatura } from "../../../types/Assinatura";
import { PagarFatura } from "./PagarFatura";
import { ListaFaturas } from "./ListaFaturas";
import { ModalMudarForma } from "./ModalMudarForma";
import { ModalAtualizarCartao } from "./ModalAtualizarCartao";
import { ViewAssinaturaNova } from "../styles";

interface AssinaturaNovaProps {
  info: InfoAssinatura;
  faturas: Fatura[];
  onRefresh: () => void;
}

const WHATSAPP_SUPORTE =
  "https://api.whatsapp.com/send?phone=555434434563&text=Ol%C3%A1,%20estou%20no%20painel%20do%20Conecta%20Venda%20e%20quero%20entender%20sobre%20minha%20assinatura.";

const formatarValor = (v: number) =>
  v.toLocaleString("pt-BR", { minimumFractionDigits: 2 });

const formatarData = (d: string | null) =>
  d ? moment(d, "YYYY-MM-DD").format("DD/MM/YYYY") : "-";

/**
 * Tela /assinatura — versão fluxo novo, redesign 2026-05.
 * 2 colunas: esquerda Plano contratado + Fatura aberta, direita Histórico + suporte
 */
export const AssinaturaNova: React.FC<AssinaturaNovaProps> = ({
  info,
  faturas,
  onRefresh,
}) => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [cancelando, setCancelando] = useState(false);
  const [cancelandoUpgrade, setCancelandoUpgrade] = useState(false);
  const [modalMudarForma, setModalMudarForma] = useState(false);
  const [modalAtualizarCartao, setModalAtualizarCartao] = useState(false);
  const [pixUpgrade, setPixUpgrade] = useState<{
    qr_code?: string | null;
    qr_code_url?: string | null;
    expires_at?: string | null;
  } | null>(null);
  const [carregandoPixUpgrade, setCarregandoPixUpgrade] = useState(false);
  const [pixUpgradeCopiado, setPixUpgradeCopiado] = useState(false);

  const handleCancelarMudanca = async () => {
    setCancelando(true);
    try {
      const res = await AssinaturaApi().cancelarMudancaPlano();
      if (res.ok) {
        await Swal.fire({ icon: "success", title: "Mudança de plano cancelada" });
        onRefresh();
      } else {
        Swal.fire({ icon: "warning", title: "Não foi possível cancelar", text: res.mensagem });
      }
    } catch (e: any) {
      console.error("Erro ao cancelar mudança:", e);
      Swal.fire({ icon: "error", title: "Erro ao cancelar a mudança" });
    } finally {
      setCancelando(false);
    }
  };

  const handleCancelarUpgrade = async () => {
    const confirma = await Swal.fire({
      icon: "question",
      title: "Cancelar upgrade?",
      text: "O PIX será cancelado e seu plano atual permanece. Você pode pedir o upgrade de novo a qualquer momento.",
      showCancelButton: true,
      confirmButtonText: "Cancelar upgrade",
      cancelButtonText: "Voltar",
    });
    if (!confirma.isConfirmed) return;
    setCancelandoUpgrade(true);
    try {
      const res = await AssinaturaApi().cancelarUpgradePendente();
      if (res.ok) {
        await Swal.fire({ icon: "success", title: "Upgrade cancelado" });
        onRefresh();
      } else {
        Swal.fire({ icon: "warning", title: "Não foi possível cancelar", text: res.mensagem || res.erro });
      }
    } catch (e: any) {
      console.error("Erro ao cancelar upgrade:", e);
      Swal.fire({ icon: "error", title: "Erro ao cancelar upgrade" });
    } finally {
      setCancelandoUpgrade(false);
    }
  };

  // Busca QR + copia-e-cola do upgrade pendente ao montar (quando ainda ativo).
  // Stone e fonte de verdade — nao gravamos QR no banco.
  const upgradeChargeId = info.assinatura?.upgrade_pendente?.charge_id;
  const upgradeDataSolic = info.assinatura?.upgrade_pendente?.data_solicitacao;
  useEffect(() => {
    if (!upgradeChargeId || !upgradeDataSolic) {
      setPixUpgrade(null);
      return;
    }
    const expirou = moment().diff(moment(upgradeDataSolic), "hours") >= 24;
    if (expirou) {
      setPixUpgrade(null);
      return;
    }
    setCarregandoPixUpgrade(true);
    AssinaturaApi()
      .consultarUpgradePendentePix()
      .then((r) => {
        if (r.ok) {
          setPixUpgrade({
            qr_code: r.qr_code ?? null,
            qr_code_url: r.qr_code_url ?? null,
            expires_at: r.expires_at ?? null,
          });
        }
      })
      .catch((e) => console.error("Erro ao consultar PIX do upgrade:", e))
      .finally(() => setCarregandoPixUpgrade(false));
  }, [upgradeChargeId, upgradeDataSolic]);

  const handleCopiarPixUpgrade = () => {
    if (pixUpgrade?.qr_code) {
      navigator.clipboard.writeText(pixUpgrade.qr_code);
      setPixUpgradeCopiado(true);
      setTimeout(() => setPixUpgradeCopiado(false), 5000);
    }
  };

  if (!info.assinatura) {
    return (
      <ViewAssinaturaNova theme={theme}>
        <div className="state erro">Dados da assinatura não encontrados.</div>
      </ViewAssinaturaNova>
    );
  }

  const { assinatura, ciclo_aberto, modo_restrito, pagamento_pendente, pode_pix } = info;

  // Mudança de forma de pagamento pelo cliente está desativada — a troca é feita
  // pelo painel admin (gerenciamento). Flag pra reativar fácil se voltar pro cliente.
  const PERMITIR_MUDAR_FORMA_CLIENTE = false;
  const { plano, cupom_aplicado, upgrade_pendente } = assinatura;

  // "Atualizar cartão" (troca pura, sem cobrar): assinatura no cartão, não
  // cancelada e sem fatura aberta. Com fatura aberta é o PagarFatura que exibe
  // o botão (lá a troca já cobra a fatura). Cobre também o caso migrado com
  // cartão inválido — antes gated por cartao_pendente.
  const podeAtualizarCartao =
    assinatura.forma_pagamento_padrao === "cartao" &&
    assinatura.status !== "cancelada" &&
    !ciclo_aberto;

  // Upgrade PIX pendente: expira em 1 dia apos data_solicitacao. UI mostra banner
  // diferente conforme estado (ativo/expirado).
  const upgradeExpirado = upgrade_pendente
    ? moment().diff(moment(upgrade_pendente.data_solicitacao), "hours") >= 24
    : false;

  const valorEfetivo =
    plano.valor_com_desconto != null ? plano.valor_com_desconto : plano.valor_cheio;
  const temDescontoPlano =
    plano.valor_com_desconto != null && plano.valor_com_desconto < plano.valor_cheio;

  // Valor com cupom — calcula no front (não vem do back, é derivável).
  let valorComCupom: number | null = null;
  if (cupom_aplicado) {
    if (cupom_aplicado.gratis) {
      valorComCupom = 0;
    } else if (cupom_aplicado.tipo_desconto === "percentual") {
      valorComCupom = valorEfetivo * (1 - cupom_aplicado.desconto / 100);
    } else {
      valorComCupom = Math.max(0, valorEfetivo - cupom_aplicado.desconto);
    }
  }

  const planoEPeriodicidade = plano.periodicidade
    ? `${plano.nome} | ${plano.periodicidade}`
    : plano.nome;

  const cupomLabel = cupom_aplicado
    ? cupom_aplicado.gratis
      ? "- 100% grátis"
      : cupom_aplicado.tipo_desconto === "percentual"
        ? `- ${cupom_aplicado.desconto}% de desconto`
        : `- R$ ${formatarValor(cupom_aplicado.desconto)} de desconto`
    : "";

  return (
    <ViewAssinaturaNova theme={theme}>
      {/* Coluna esquerda */}
      <div className="coluna-esquerda">
        <div className="secao-titulo">Plano contratado</div>

        <div className="bloco">
          <div className="bloco-forms">
            {(pagamento_pendente || modo_restrito) && (
              <div className="alert vermelho">
                <div className="alert-conteudo">
                  <div className="alert-titulo">
                    {modo_restrito ? "Conta restrita" : "Pagamento pendente"}
                  </div>
                  <div className="alert-texto">
                    {modo_restrito
                      ? "Não identificamos o pagamento da sua assinatura. Por isso, o acesso à sua conta foi restrito temporariamente, regularize abaixo para voltar a acessar."
                      : "Tivemos um problema com seu último pagamento. Regularize abaixo para evitar o bloqueio de acesso."}
                  </div>
                </div>
              </div>
            )}

            {assinatura.mudanca_agendada && (
              <div className="alert amarelo">
                <div className="alert-conteudo">
                  <div className="alert-titulo">Mudança de plano agendada</div>
                  <div className="alert-texto">
                    Seu plano mudará para <strong>{assinatura.mudanca_agendada.nome}</strong> em{" "}
                    <strong>{formatarData(assinatura.proximo_vencimento)}</strong>. Você pode cancelar a
                    qualquer momento antes desta data.
                  </div>
                  <button
                    type="button"
                    className="alert-link"
                    onClick={handleCancelarMudanca}
                    disabled={cancelando}
                  >
                    {cancelando ? "Cancelando..." : "Cancelar mudança"}
                  </button>
                </div>
              </div>
            )}

            {upgrade_pendente && !upgradeExpirado && (
              <div
                className="alert amarelo"
                style={{
                  backgroundColor: "transparent",
                  padding: "calc(0.4rem + 5px) calc(0.8rem + 5px)",
                }}
              >
                <div className="alert-conteudo">
                  <div className="alert-titulo" style={{ marginBottom: "5px" }}>
                    Upgrade aguardando pagamento
                  </div>
                  <div className="alert-texto">
                    Pague <strong>R$ {formatarValor(upgrade_pendente.valor_prorata)}</strong> via PIX
                    para mudar para <strong>{upgrade_pendente.plano_nome}</strong>. O upgrade é
                    efetivado automaticamente após o pagamento.
                  </div>

                  {carregandoPixUpgrade && (
                    <div className="alert-texto" style={{ marginTop: "0.75rem" }}>
                      Carregando QR Code...
                    </div>
                  )}

                  {pixUpgrade?.qr_code_url && (
                    <div style={{ marginTop: "1rem", textAlign: "center" }}>
                      <img
                        src={pixUpgrade.qr_code_url}
                        alt="QR Code PIX do upgrade"
                        style={{ maxWidth: "150px", display: "inline-block" }}
                      />
                    </div>
                  )}

                  {pixUpgrade?.qr_code && (
                    <>
                      <div className="alert-texto" style={{ marginTop: "0.75rem" }}>
                        Ou copie o código PIX:
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          fontSize: "11px",
                          background: "rgba(0,0,0,0.05)",
                          padding: "0.5rem 2rem",
                          borderRadius: "4px",
                          marginTop: "0.25rem",
                        }}
                      >
                        <span style={{ wordBreak: "break-all", flex: 1 }}>
                          {pixUpgrade.qr_code}
                        </span>
                        <button
                          type="button"
                          onClick={handleCopiarPixUpgrade}
                          disabled={pixUpgradeCopiado}
                          title="Copiar código PIX"
                          style={{
                            background: "none",
                            border: "none",
                            cursor: pixUpgradeCopiado ? "default" : "pointer",
                            padding: "0.25rem",
                            display: "flex",
                            alignItems: "center",
                            color: "#c87a00",
                            fontSize: "11px",
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                            flexShrink: 0,
                          }}
                        >
                          {pixUpgradeCopiado ? (
                            "código copiado!"
                          ) : (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </>
                  )}

                  <button
                    type="button"
                    className="alert-link"
                    onClick={handleCancelarUpgrade}
                    disabled={cancelandoUpgrade}
                  >
                    {cancelandoUpgrade ? "Cancelando..." : "Cancelar upgrade"}
                  </button>
                </div>
              </div>
            )}

            {upgrade_pendente && upgradeExpirado && (
              <div className="alert amarelo">
                <div className="alert-conteudo">
                  <div className="alert-titulo">Upgrade expirado</div>
                  <div className="alert-texto">
                    O prazo para pagar o PIX do upgrade para{" "}
                    <strong>{upgrade_pendente.plano_nome}</strong> expirou. Cancele e refaça
                    para gerar um novo PIX com o valor atualizado.
                  </div>
                  <button
                    type="button"
                    className="alert-link"
                    onClick={handleCancelarUpgrade}
                    disabled={cancelandoUpgrade}
                  >
                    {cancelandoUpgrade ? "Cancelando..." : "Cancelar upgrade"}
                  </button>
                </div>
              </div>
            )}

            <div className="linha-dupla">
              <div className="campo">
                <span className="campo-label">Plano e periodicidade</span>
                <div className="campo-valor">{planoEPeriodicidade}</div>
              </div>
              <div className="campo">
                <span className="campo-label">Valor</span>
                <div className="campo-valor">
                  {temDescontoPlano && (
                    <span className="valor-riscado">R$ {formatarValor(plano.valor_cheio)}</span>
                  )}
                  R$ {formatarValor(valorEfetivo)}
                </div>
              </div>
            </div>

            {cupom_aplicado && (
              <div className="linha-dupla">
                <div className="campo">
                  <span className="campo-label">
                    Cupom <span className="cupom-badge">{cupom_aplicado.nome}</span>
                  </span>
                  <div className="campo-valor">{cupomLabel}</div>
                </div>
                <div className="campo">
                  <span className="campo-label">Valor com desconto</span>
                  <div className="campo-valor">
                    R$ {formatarValor(valorComCupom ?? valorEfetivo)}
                  </div>
                </div>
              </div>
            )}

            <p className="proximo-vencimento">
              Próximo vencimento: {formatarData(assinatura.proximo_vencimento)}
            </p>
          </div>

          <div className="botoes-acoes">
          <button
            type="button"
            className="botao-trocar-plano"
            onClick={() => {
              if (upgrade_pendente) {
                Swal.fire({
                  icon: "info",
                  title: "Upgrade aguardando pagamento",
                  text: "Você tem um upgrade aguardando pagamento. Conclua ou cancele para trocar de plano novamente.",
                  confirmButtonText: "OK",
                });
                return;
              }
              navigate("/assinatura/trocar-plano");
            }}
          >
            Trocar de plano
          </button>
          {PERMITIR_MUDAR_FORMA_CLIENTE && (assinatura.forma_pagamento_padrao === "pix" || pode_pix) && (
            <button
              type="button"
              className="botao-trocar-plano"
                onClick={() => setModalMudarForma(true)}
            >
              {assinatura.forma_pagamento_padrao === "cartao"
                ? "Mudar para PIX"
                : "Mudar para cartão"}
            </button>
          )}
          {/* "Atualizar cartão": troca pura (modal sem valorCobranca = não cobra).
              Regras em `podeAtualizarCartao`. */}
          {podeAtualizarCartao && (
            <button
              type="button"
              className="botao-trocar-plano"
                onClick={() => setModalAtualizarCartao(true)}
            >
              Atualizar cartão
            </button>
          )}
          </div>
        </div>

        {ciclo_aberto && (
          <>
            <div className="divisor-horizontal" />
            <PagarFatura cicloAberto={ciclo_aberto} onSucesso={onRefresh} maxParcelas={assinatura.plano.parcelas} />
          </>
        )}
      </div>

      {/* Divisor vertical */}
      <div className="divisor-vertical" />

      {/* Coluna direita */}
      <div className="coluna-direita">
        <div>
          <div className="secao-titulo" style={{ marginBottom: "3rem" }}>Histórico de faturas</div>
          <ListaFaturas faturas={faturas} />
        </div>

        <div className="suporte">
          <p className="suporte-texto">Está com dúvida? Entre em contato no botão abaixo.</p>
          <a
            className="suporte-botao"
            href={WHATSAPP_SUPORTE}
            target="_blank"
            rel="noopener noreferrer"
          >
            Falar com o atendimento
          </a>
        </div>
      </div>

      <ModalMudarForma
        isOpen={modalMudarForma}
        setOpen={setModalMudarForma}
        formaAtual={assinatura.forma_pagamento_padrao}
        onSucesso={onRefresh}
      />

      <ModalAtualizarCartao
        isOpen={modalAtualizarCartao}
        setOpen={setModalAtualizarCartao}
        onSucesso={onRefresh}
      />
    </ViewAssinaturaNova>
  );
};

export default AssinaturaNova;
